import { useNavigation } from 'app/hooks/navigation';
import { Event, track } from 'app/lib/analytics';
import { hasNotificationPermission } from 'app/lib/notifications';
import { isQuickOffer } from 'app/network/listing';
import { useGetInterlocutor, useGetMyselfInChannel } from 'app/network/chat';
import { MESSAGE_ID_LOADING, useCreateOneMessage } from 'app/network/chat/message';
import type { Channel, MemberChannel, Message } from 'app/types/chat';
import { MessageKind } from 'app/types/chat';
import type { Listing } from 'app/types/listing';
import { isMobileNative } from 'app/utils/web-helpers';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

export const useChannelToolbar = ({
  channel,
  listing,
  setMessageBeingSent,
  onSupportReplyQueued,
}: {
  channel?: Channel;
  listing?: Listing;
  setMessageBeingSent: Dispatch<SetStateAction<Message | undefined>>;
  onSupportReplyQueued?: (requestMessageId: string) => void;
}) => {
  const getMyselfInChannel = useGetMyselfInChannel();
  const navigation = useNavigation();
  const getInterlocutor = useGetInterlocutor();
  const [drafts, setDrafts] = useState(() => new Map<string, string>());
  const channelUrl = channel?.channel_url;
  const textContent = channelUrl ? (drafts.get(channelUrl) ?? '') : '';

  const setTextContent: Dispatch<SetStateAction<string>> = (value) => {
    if (!channelUrl) return;
    setDrafts((current) => {
      const nextText = typeof value === 'function' ? value(current.get(channelUrl) ?? '') : value;
      const next = new Map(current);
      if (nextText) {
        next.set(channelUrl, nextText);
      } else {
        next.delete(channelUrl);
      }
      return next;
    });
  };
  const interlocutor = getInterlocutor(channel);

  const onMessageSent = (message: string) => {
    setTextContent('');
    const myself = getMyselfInChannel(channel);
    if (!myself) return;
    setMessageBeingSent({
      message,
      type: MessageKind.Message,
      channel_url: channel!.channel_url,
      created_at: Date.now(),
      user: myself,
      viewed: false,
      message_id: MESSAGE_ID_LOADING,
    });
  };

  const createMessage = useCreateOneMessage({
    async onSuccess(data) {
      if (data.supportReply?.status === 'queued') {
        onSupportReplyQueued?.(data.supportReply.requestMessageId);
      }
      const hasGrantedNotificationPermission = await hasNotificationPermission();
      const isFirstMessageInChannel = channel && !channel.last_message;
      const shouldAskForNotificationPermission =
        isFirstMessageInChannel && !hasGrantedNotificationPermission && isMobileNative;

      if (listing) {
        track(Event.SendMessage, {
          is_first: isFirstMessageInChannel ? 'y' : 'n', // Should be a boolean, but was a y/n string to begin with; keeping it like that to prevent errors during analysis on mixpanel.com
          listing_id: listing.id,
          listing_type: listing.type,
          listing_category: listing.categoryPrimary.id,
          listing_subtype: listing.subtype,
          listing_service_category: listing.serviceCategory?.id,
          is_quick_offer: isQuickOffer(listing),
        });
      } else if (channel && 'data' in channel && channel.data && 'userId' in channel.data) {
        track(Event.SendMessageToMember, {
          is_first: isFirstMessageInChannel ? 'y' : 'n', // Should be a boolean, but was a y/n string to begin with; keeping it like that to prevent errors during analysis on mixpanel.com
          user_id: (channel as MemberChannel).data.userId,
        });
      } else if (channel) {
        track(Event.SendMessageToSupport);
      }

      if (shouldAskForNotificationPermission) {
        setTimeout(
          () =>
            navigation.navigate(
              'notification-explainer',
              {
                context: 'chat',
                interlocutor,
              },
              { pop: true },
            ),
          1000,
        );
      }
    },
  });

  const createTextMessage = (message: string, parentMessageId?: string) => {
    const trimmed = message.trim();
    if (trimmed === '' || !channel) return;

    createMessage.reset();
    createMessage.mutate(
      {
        channel_url: channel.channel_url,
        message: trimmed,
        type: MessageKind.Message,
        isFirstMessage: !channel.last_message,
        listingUserId: getInterlocutor(channel)?.user_id,
        ...(parentMessageId ? { reply_to_id: parentMessageId } : {}),
      },
      {
        onError: () => {
          setMessageBeingSent(undefined);
          setTextContent((current) => (current.trim() ? current : trimmed));
        },
      },
    );
    onMessageSent(trimmed);
  };

  return { createMessage, createTextMessage, onMessageSent, textContent, setTextContent, getMyselfInChannel };
};
